import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { boxSizingStyles } from '../../styles/lit-shared';

@customElement('ui-modal')
export class UiModal extends LitElement {
  static styles = [
    boxSizingStyles,
    css`
    :host {
      display: contents;
    }

    .backdrop {
      position: fixed;
      inset: 0;
      z-index: 200;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgb(6 6 15 / 75%);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      padding: var(--space-lg);
      animation: backdrop-in var(--duration-normal) var(--ease-out) both;
    }

    @keyframes backdrop-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .dialog {
      position: relative;
      width: 100%;
      max-width: 520px;
      max-height: 90dvh;
      overflow-y: auto;
      border-radius: var(--radius-lg);
      background: var(--palette-ink-900);
      border: 1px solid var(--color-border-strong);
      box-shadow: var(--shadow-lg), var(--shadow-glow-primary);
      animation: dialog-in var(--duration-normal) var(--ease-out) both;
    }

    @keyframes dialog-in {
      from {
        opacity: 0;
        transform: translateY(16px) scale(0.97);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    .dialog::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      padding: 1px;
      background: var(--gradient-border);
      -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
      opacity: 0.5;
    }

    .dialog-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--space-lg) var(--space-xl);
      border-bottom: 1px solid var(--color-border);
    }

    .dialog-title {
      font-size: 1.125rem;
      font-weight: 700;
      letter-spacing: -0.02em;
      background: var(--gradient-text);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .close-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border: none;
      border-radius: var(--radius-sm);
      background: transparent;
      color: var(--color-text-muted);
      cursor: pointer;
      font-size: 1.125rem;
      line-height: 1;
      transition:
        background var(--duration-fast) var(--ease-out),
        color var(--duration-fast) var(--ease-out);
    }

    .close-btn:hover {
      background: var(--color-surface-hover);
      color: var(--color-text);
    }

    .dialog-body {
      padding: var(--space-xl);
    }

    /* --- Mobile: bottom sheet --- */
    @media (max-width: 639px) {
      .backdrop {
        align-items: flex-end;
        padding: 0;
      }

      .dialog {
        max-width: 100%;
        max-height: 85dvh;
        border-radius: var(--radius-lg) var(--radius-lg) 0 0;
        animation: sheet-in var(--duration-normal) var(--ease-out) both;
      }

      @keyframes sheet-in {
        from {
          opacity: 0;
          transform: translateY(100%);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    }
  `,
  ];

  @property({ type: Boolean, reflect: true }) open = false;
  @property({ type: String }) title = '';

  private close() {
    this.dispatchEvent(
      new CustomEvent('modal-close', { bubbles: true, composed: true }),
    );
  }

  private handleBackdropClick(e: Event) {
    if (e.target === e.currentTarget) this.close();
  }

  private handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') this.close();
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this._boundKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._boundKeydown);
  }

  private _boundKeydown = (e: KeyboardEvent) => this.handleKeydown(e);

  render() {
    if (!this.open) return html``;

    return html`
      <div class="backdrop" @click=${this.handleBackdropClick}>
        <div class="dialog" role="dialog" aria-modal="true" aria-label=${this.title}>
          <div class="dialog-header">
            <span class="dialog-title">${this.title}</span>
            <button
              class="close-btn"
              aria-label="Cerrar"
              @click=${this.close}
            >✕</button>
          </div>
          <div class="dialog-body">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ui-modal': UiModal;
  }
}
